// useProfileAccounts.js
import { useState, useEffect, useRef, useContext } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { LoggedInUserContext } from "../contexts/LoggedInUser";

export default function useProfileAccounts(userId) {
  const [userAccounts, setUserAccounts] = useState([]);
  const [ready, setReady] = useState(false);      // auth/session ready
  const inFlight = useRef(false);
  const isMounted = useRef(true);

  // If no userId is provided, prefer the accounts from LoggedInUserContext (no extra API)
  const { loggedInUserProfile } = useContext(LoggedInUserContext) || {};

  // Track auth state; only set ready=true when a session exists
  useEffect(() => {
    isMounted.current = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (isMounted.current) setReady(true);
      } catch {
        if (isMounted.current) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (isMounted.current) setReady(true);
        } catch {
          if (isMounted.current) setReady(false);
        }
      } else if (evt === "signOut") {
        if (isMounted.current) {
          setReady(false);
          setUserAccounts([]); // clear on sign out
        }
      }
    });

    return () => {
      isMounted.current = false;
      unsub();
    };
  }, []);

  // If we're showing the current user and context already has accounts, use them
  useEffect(() => {
    if (!userId && loggedInUserProfile?.accounts) {
      setUserAccounts(loggedInUserProfile.accounts || []);
    }
  }, [userId, loggedInUserProfile?.accounts]);

  // Fetch only when authed (ready) or when a specific userId is passed
  useEffect(() => {
    if (!ready) return;                 // not authenticated yet → do nothing
    if (!userId && loggedInUserProfile?.accounts) return; // already satisfied by context

    let cancelled = false;
    const fetchData = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const path = userId ? `/profile/${userId}` : "/profile";
        const { accounts } = await API.get("AccountGatewayApi", path);
        if (!cancelled && isMounted.current) {
          setUserAccounts(accounts || []);
        }
      } catch (e) {
        // optional: console.error("useProfileAccounts fetch error", e);
      } finally {
        inFlight.current = false;
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [ready, userId, loggedInUserProfile?.accounts]);

  return userAccounts;
}
